import { Action, AnyAction } from 'redux';

import { COLLECTIONS, RequestId } from 'appConstants';
import { ApiRequestError } from 'services';

/**
 * RequestsManager
 *
 * The RequestsManager is a reducer that allows for storing any in-progress API
 * request's state within the Redux global state so that any connected component
 * maybe be updated with that request's state anytime it changes (ex. from
 * fetching to success).
 *
 * It is also possible to imperitively retrive a request's state via the Redux
 * store.getState().Requests[<id>], with the appropriate Request ID.
 */

/* Types */

export interface RetryableAction {
  readonly retryNow?: () => void;
  readonly wait?: number;
}

export interface AbortRequestAction {
  readonly controller?: AbortController;
}

export interface RequestAction
  extends Action,
    RetryableAction,
    AbortRequestAction {
  readonly error?: ApiRequestError;
  readonly id: string;
}

export interface RequestState {
  readonly error: ApiRequestError;
  readonly hasAlert: boolean;
  readonly hasError: boolean;
  readonly isComplete: boolean;
  readonly isFetching: boolean;
  readonly isPristine: boolean;
  readonly isRetrying: boolean;
  readonly isAborted: boolean;
  readonly isSuccess: boolean;
  readonly retryAttempt: number;
  readonly retryNow: () => void;
  readonly wait: number;
  readonly controller: AbortController;
}

export type RequestsManager = { [key: string]: RequestState };

/* Initial State */

const defaultState: RequestState = {
  error: {} as ApiRequestError,
  hasAlert: false,
  hasError: false,
  isComplete: false,
  isFetching: false,
  isPristine: true,
  isRetrying: false,
  isAborted: false,
  isSuccess: false,
  retryAttempt: 0,
  retryNow: () => null,
  wait: 0,
  controller: {} as AbortController,
};

// Initialize all known requests and collection requests to a default state
const allKeys = [...Object.keys(RequestId), ...Object.keys(COLLECTIONS)];
const initialState: RequestsManager = allKeys.reduce((acc, id) => {
  acc[id] = { ...defaultState };
  return acc;
}, {});

const getRequestId = (actionType: string, requestType: string) =>
  actionType.replace(`_${requestType}`, '');

/* Actions */

enum Actions {
  SET_REQUEST_ERROR = 'SET_REQUEST_ERROR',
  SET_REQUEST_ABORT = 'SET_REQUEST_ABORT',
  SET_REQUEST_RETRY = 'SET_REQUEST_RETRY',
  SET_REQUEST_START = 'SET_REQUEST_START',
  SET_REQUEST_SUCCESS = 'SET_REQUEST_SUCCESS',
  SET_REQUEST_TO_DEFAULT = 'SET_REQUEST_TO_DEFAULT',
}

const setRequestError = (
  id: string,
  error?: ApiRequestError,
): RequestAction => ({
  type: Actions.SET_REQUEST_ERROR,
  error,
  id,
});

const setAbortError = (id: string, error?: ApiRequestError): RequestAction => ({
  type: Actions.SET_REQUEST_ABORT,
  error,
  id,
});

const setRequestRetry = (
  id: string,
  wait?: number,
  retryNow?: () => void,
): RequestAction => ({
  type: Actions.SET_REQUEST_RETRY,
  id,
  wait,
  retryNow,
});

const setRequestStart = (
  id: string,
  controller?: AbortController,
): RequestAction => ({
  type: Actions.SET_REQUEST_START,
  id,
  controller,
});

const setRequestSuccess = (id: string): RequestAction => ({
  type: Actions.SET_REQUEST_SUCCESS,
  id,
});

const setRequestToDefault = (id: string): RequestAction => ({
  type: Actions.SET_REQUEST_TO_DEFAULT,
  id,
});

/* Reducer */

const requestsManagerReducer = (state = initialState, action: AnyAction) => {
  const requestType = action.type.split('_').pop();
  const requestId = action.id || getRequestId(action.type, requestType);
  const itemState = state[requestId];

  switch (requestType) {
    case 'ERROR':
    case Actions.SET_REQUEST_ERROR:
      return {
        ...state,
        [requestId]: {
          ...itemState,
          error: action.error,
          hasAlert: true,
          hasError: true,
          isComplete: true,
          isFetching: false,
          isSuccess: false,
        },
      };
    case 'ABORT':
    case Actions.SET_REQUEST_ABORT:
      return {
        ...state,
        [requestId]: {
          ...itemState,
          error: action.error,
          isSuccess: false,
          isAborted: true,
        },
      };
    case 'RETRY':
    case Actions.SET_REQUEST_RETRY:
      return {
        ...state,
        [requestId]: {
          ...itemState,
          hasAlert: true,
          isRetrying: true,
          isSuccess: false,
          retryAttempt: itemState.retryAttempt + 1,
          retryNow: action.retryNow ? action.retryNow : null,
          wait: action.wait,
        },
      };
    case 'START':
    case Actions.SET_REQUEST_START:
      return {
        ...state,
        [requestId]: {
          ...itemState,
          isFetching: true,
          isPristine: false,
          hasError: false,
          controller: action.controller,
        },
      };
    case 'SUCCESS':
    case Actions.SET_REQUEST_SUCCESS:
      return {
        ...state,
        [requestId]: {
          ...itemState,
          hasAlert: false,
          hasError: false,
          isComplete: true,
          isFetching: false,
          isSuccess: true,
        },
      };
    case 'DEFAULT':
    case Actions.SET_REQUEST_TO_DEFAULT:
      return {
        ...state,
        [requestId]: { ...defaultState },
      };
    default:
      return state;
  }
};

export {
  requestsManagerReducer,
  setRequestError,
  setAbortError,
  setRequestRetry,
  setRequestStart,
  setRequestSuccess,
  setRequestToDefault,
};
